/**
 * §11.2 — the final palette. Every colour the renderer draws resolves through this
 * module; a raw hex literal anywhere else in `src/render/**` is a bug.
 *
 * Tokens are exported three ways:
 *  - as named string constants (§11.2 convention `pal.<family>.<member>`),
 *  - as the `PALETTE` token → hex table (used by the validator and by content refs),
 *  - as a linear-RGB float table indexed by `tokenIndex()`, which is what the hot
 *    path reads so no per-frame parsing or allocation happens.
 *
 * §11.12 colourblind presets remap accent hexes only; shape and motion codes never
 * change, so the preset switch rebuilds `LINEAR` and nothing else.
 */

// --- §11.2.1 reserved families (spine §12) ---

export const PAL_PLAYER_CORE = '#FFF6DC';
export const PAL_PLAYER_AURA = '#FFD36B';
export const PAL_XP_MOTE = '#3DFFC4';
export const PAL_HEALTH_PICKUP = '#FF4D6B';
export const PAL_HAZARD_FILL = '#FF2FD6';
export const PAL_HAZARD_STRIPE = '#FFFFFF';
export const PAL_WARNING = '#FF2FD6';
export const PAL_PORTAL = '#BFE8FF';
export const PAL_ELITE_OUTLINE = '#FFFFFF';

// --- §11.2.2 friendly attacks, warm spectrum ---

export const PAL_FRIENDLY_LANCE = '#FFC84D';
export const PAL_FRIENDLY_KRILL = '#FF9E2E';
export const PAL_FRIENDLY_GAZE = '#FFE3A3';
export const PAL_FRIENDLY_HALO = '#FFB85C';
export const PAL_FRIENDLY_NOVA = '#FF7A59';
export const PAL_FRIENDLY_MINE = '#FF8A1F';
export const PAL_FRIENDLY_CHAIN = '#FFAB6E';
export const PAL_FRIENDLY_SCION = '#FFF3E0';
export const PAL_FRIENDLY_POOL = '#E08A4E';
export const PAL_FRIENDLY_SHROUD = '#FF9E8A';
export const PAL_FRIENDLY_MAW = '#FF9633';
export const PAL_FRIENDLY_SWEEP = '#FF6B1A';

/**
 * --- §11.2.3 enemy archetypes, cool spectrum ---
 *
 * AMD-41 — the roster is re-pitched from cyan→violet to BLUE→violet.
 *
 * The ratified §11.2.3 hexes put seven of fourteen archetypes within 45° of
 * `pal.xp.mote` (#3DFFC4, hue 161.8°): brood-bell 17.8°, bloom-jelly 21.0°,
 * driftfin 27.5°, glimmerfry 27.6°, aegis-medusa 28.0°, siphon-leech 39.1°,
 * dart-eel 50.6°. Spine §12 reserves green-cyan for XP and cyan/blue/violet for
 * enemies, and the two reservations were allowed to touch. At 390 px, under a
 * 0.85-strength bloom, they read as one family — a playtester described the motes
 * as "green energy tokens that fall out of" the enemies, which is the threat/reward
 * contract failing at the most basic level.
 *
 * Two directions were available. Moving the XP family is not one of them: spine §12
 * fixes XP at green-cyan and the spine wins conflicts, so the enemy arc is the side
 * with slack. Every hue therefore rotates into [224°, 278°]; the floor is set by the
 * §11.15-style separation rule below (Δhue ≥ 55° from the mote), and the ceiling
 * stops short of the magenta hazard reservation at 300°.
 *
 * Each archetype keeps its ratified HSL lightness and saturation, so "pale cyan"
 * became pale blue and "deep teal" became deep cobalt — the §11.2.3 luminance-step
 * mitigation for the near-collision cluster survives the move intact. Three
 * exceptions come DOWN in lightness: glimmerfry 0.75→0.66, siphon-leech 0.78→0.71
 * and aegis-medusa 0.83→0.76. At their ratified lightness those three were
 * effectively white, which is why aegis-medusa measured OKLab ΔE 11.2 against
 * `pal.player.core` and 7.8 under the protanopia preset — an enemy that a
 * protanope could not tell from the player's own body. The ordered ladder
 * (0.66 < 0.71 < 0.76) is preserved, so the cluster still separates by luminance.
 *
 * Measured, in OKLab ΔE (×100), worst pair across every enemy × every pickup family
 * under all four §11.12 presets, each simulated through its own deficiency:
 *
 *   preset          before   after
 *   off              11.2     22.1
 *   deuteranopia     10.9     22.9
 *   protanopia        7.8     27.5
 *   tritanopia        9.2     16.5
 *
 * Intra-roster separation improves too: the closest pair of enemies goes from
 * ΔE 3.4 (dart-eel|colonial-curtain) to 4.1 (driftfin|glimmerfry).
 *
 * `content/enemies.json` carries the same hexes, so this change moves `content_hash`
 * and invalidates existing replays and snapshots. That is the correct behaviour for
 * a content edit (A.6) and is expected here.
 */

export const PAL_ENEMY_DRIFTFIN = '#3D68F5';
export const PAL_ENEMY_GLIMMERFRY = '#5274FF';
export const PAL_ENEMY_DART_EEL = '#2E35FF';
export const PAL_ENEMY_VENTSPITTER = '#B35CFF';
export const PAL_ENEMY_BLOOM_JELLY = '#2E49E0';
export const PAL_ENEMY_BASALT_CRAB = '#593389';
export const PAL_ENEMY_SPEARNOSE = '#5123E6';
export const PAL_ENEMY_NETTLE_ANEMONE = '#925CFF';
export const PAL_ENEMY_AEGIS_MEDUSA = '#858DFF';
export const PAL_ENEMY_SIPHON_LEECH = '#7470FA';
export const PAL_ENEMY_GLOOMSQUID = '#534AA5';
export const PAL_ENEMY_LUREMAW = '#CD7DFD';
export const PAL_ENEMY_COLONIAL_CURTAIN = '#775CFF';
export const PAL_ENEMY_BROOD_BELL = '#0C3198';

// --- §11.2.4 bosses, bi-chromatic ---

export const PAL_BOSS_LANTERN_TYRANT_PRIMARY = '#7A3FFF';
export const PAL_BOSS_LANTERN_TYRANT_SECONDARY = '#35E0FF';
export const PAL_BOSS_ABYSSAL_WARDEN_PRIMARY = '#3B5BDB';
export const PAL_BOSS_ABYSSAL_WARDEN_SECONDARY = '#A9F1FF';
export const PAL_BOSS_CROWN_MEDUSA_PRIMARY = '#3550C8';
export const PAL_BOSS_CROWN_MEDUSA_SECONDARY = '#A9F1FF';
export const PAL_BOSS_HADAL_SOVEREIGN_PRIMARY = '#7A3FFF';
export const PAL_BOSS_HADAL_SOVEREIGN_SECONDARY = '#35E0FF';

// --- §11.2.5 status effects ---

export const PAL_STATUS_BURN = '#FF6B3D';
export const PAL_STATUS_POISON = '#A6FF4D';
export const PAL_STATUS_SHOCK = '#D8F4FF';
export const PAL_STATUS_SLOW = '#CFEFFF';
export const PAL_STATUS_STUN = '#FFFFFF';
export const PAL_STATUS_WEAKEN = '#FF7A59';
export const PAL_STATUS_CORRODE = '#E08A4E';
export const PAL_STATUS_BLEED = '#E8552F';

// --- §11.2.6 UI ---

export const PAL_UI_BG = '#050A14';
export const PAL_UI_PANEL = '#0A1B2E';
/** AMD-6 D13: lifted from #12324A, which measured ~1.3:1 against the abyssal floor. */
export const PAL_UI_GRID = '#16324A';
export const PAL_UI_TEXT = '#E8F4FF';
export const PAL_UI_TEXTDIM = '#8FB3CC';
export const PAL_UI_ACCENT = '#FFD36B';
export const PAL_UI_DECOR = '#1E5B7A';
export const PAL_UI_HP = '#FF4D6B';
export const PAL_UI_XP = '#3DFFC4';

// --- AMD-6 additions (art-direction pass; appended, never reordered) ---

/** Major sonar grid line, drawn every 5 minor cells (D13). */
export const PAL_UI_GRID_MAJOR = '#1E4A6B';
/** Neon core stroke and the post-bloom player rim (D4, D5). Pure white by definition. */
export const PAL_UI_CORE = '#FFFFFF';
/** Damage-number outline. Black, so the ramp survives any background (D8). */
export const PAL_UI_OUTLINE = '#000000';

/**
 * R6 weapon-range ring — the §5.8 acquisition clamp, drawn.
 *
 * Deliberately a member of the sonar-instrument family (`pal.ui.grid` #16324A,
 * `pal.ui.gridmajor` #1E4A6B) and not of any entity family: one step brighter than the
 * major grid line, so it reads as the same apparatus that draws the lattice rather than
 * as something alive. It is a long way from `pal.hazard.fill` magenta (§11.9 reserves
 * that for danger), carries no warm cast that could compete with the player, and its
 * luminance ceiling is low enough that the ring never bloom-thresholds (§11.1).
 */
export const PAL_UI_RANGE = '#2E6E92';

/**
 * --- captain complexion ramp (AMD-42) ---
 *
 * §11.2 had no skin because until the captain feature there were no faces. The portrait
 * ramp in `src/captain/anchors.ts` therefore drew complexion out of §11.2.6, and §11.2.6
 * is one hue: seven of the eight chromatic entries a portrait can reach sit between
 * 200.2° and 220.0°, and 88.9% of a portrait's 1 919 opaque pixels come from six of them.
 * Every captain rendered sonar blue. The fourteen "skin tones" in `src/captain/parts.ts`
 * were fourteen triples of the same six blues — exposure levels, not complexions.
 *
 * Eight values, monotonic in displayed luminance, hue drifting 22° → 12° as it lightens:
 * heavily pigmented skin is the yellow-brown end (eumelanin) and pale skin is the rosy
 * end (haemoglobin reading through), so the drift is the direction real skin runs.
 *
 * Four constraints the values were chosen against:
 *
 *  - **Separated from every reserved warm by CHROMA, not hue.** HSL saturation 0.33–0.40
 *    against the friendly arc's 1.00. Worst OKLab ΔE against any reserved or gameplay
 *    token, trichromat, is 8.0 (`pal.skin.7` vs `pal.friendly.shroud`); the three §11.12
 *    deficiencies measure 5.5 / 5.7 / 4.3. Those are colour distances only — no captain
 *    portrait shares a frame REGION with an arena entity, so the separation that carries
 *    the load here is spatial. Nothing in `pal.skin.*` is a gameplay code.
 *
 *  - **Seven of eight never bloom.** `PostStack.BRIGHT_PASS_FLOOR` is 0.72 on the max
 *    channel. `pal.skin.0`…`pal.skin.6` top out at 0.702, so a cheek is never a lamp.
 *    `pal.skin.7` at 0.867 crosses on purpose: it is the specular, the same budget
 *    `pal.ui.core` already has, and `parts.ts` guards it the way it guards `pal.ui.core`.
 *
 *  - **Every base clears the edge-contrast floor.** `RC.minEdgeContrast` is 0.10 of
 *    displayed luminance against `pal.ui.bg`. `pal.skin.1` up measure 0.147…0.705.
 *    `pal.skin.0` measures 0.077 and does NOT clear it, so it is a shadow value only and
 *    never a base — the same rule the blue ramp's own dark end already obeys.
 *
 *  - **It does not swallow `pal.ui.accent`.** The portrait spends exactly one warm value
 *    per captain (iris, cap band, collar cord or rank flash) and that scarcity is the
 *    whole attention budget of the face. The first draft of this ramp put its highlight
 *    at #EFCDB4, a tan, which measures OKLab ΔE 9.1 from `pal.ui.accent` and 5.1 under
 *    tritanopia — a captain's lit cheek and their gold rank flash would have read as one
 *    colour. Pulling the highlight rosy (hue 25° → 12°) moves that to 13.1 / 13.4 / 13.1
 *    / 10.8 across the four presets, which is why the ramp needs no `CB_REMAP` entry:
 *    the collision is fixed in the base hex rather than papered over per preset. §11.12
 *    rule 3 remaps accent hexes only, and complexion is not an accent.
 */

export const PAL_SKIN_0 = '#291B13';
export const PAL_SKIN_1 = '#432B1E';
export const PAL_SKIN_2 = '#5C3A29';
export const PAL_SKIN_3 = '#734B36';
export const PAL_SKIN_4 = '#8B5B44';
export const PAL_SKIN_5 = '#A26A52';
export const PAL_SKIN_6 = '#B37B65';
export const PAL_SKIN_7 = '#DDB9B0';

/** Token → default hex. Insertion order defines `tokenIndex()` and never changes. */
export const PALETTE: Readonly<Record<string, string>> = {
  'pal.player.core': PAL_PLAYER_CORE,
  'pal.player.aura': PAL_PLAYER_AURA,
  'pal.xp.mote': PAL_XP_MOTE,
  'pal.health.pickup': PAL_HEALTH_PICKUP,
  'pal.hazard.fill': PAL_HAZARD_FILL,
  'pal.hazard.stripe': PAL_HAZARD_STRIPE,
  'pal.warning': PAL_WARNING,
  'pal.portal': PAL_PORTAL,
  'pal.elite.outline': PAL_ELITE_OUTLINE,

  'pal.friendly.lance': PAL_FRIENDLY_LANCE,
  'pal.friendly.krill': PAL_FRIENDLY_KRILL,
  'pal.friendly.gaze': PAL_FRIENDLY_GAZE,
  'pal.friendly.halo': PAL_FRIENDLY_HALO,
  'pal.friendly.nova': PAL_FRIENDLY_NOVA,
  'pal.friendly.mine': PAL_FRIENDLY_MINE,
  'pal.friendly.chain': PAL_FRIENDLY_CHAIN,
  'pal.friendly.scion': PAL_FRIENDLY_SCION,
  'pal.friendly.pool': PAL_FRIENDLY_POOL,
  'pal.friendly.shroud': PAL_FRIENDLY_SHROUD,
  'pal.friendly.maw': PAL_FRIENDLY_MAW,
  'pal.friendly.sweep': PAL_FRIENDLY_SWEEP,

  'pal.enemy.driftfin': PAL_ENEMY_DRIFTFIN,
  'pal.enemy.glimmerfry': PAL_ENEMY_GLIMMERFRY,
  'pal.enemy.dart-eel': PAL_ENEMY_DART_EEL,
  'pal.enemy.ventspitter': PAL_ENEMY_VENTSPITTER,
  'pal.enemy.bloom-jelly': PAL_ENEMY_BLOOM_JELLY,
  'pal.enemy.basalt-crab': PAL_ENEMY_BASALT_CRAB,
  'pal.enemy.spearnose': PAL_ENEMY_SPEARNOSE,
  'pal.enemy.nettle-anemone': PAL_ENEMY_NETTLE_ANEMONE,
  'pal.enemy.aegis-medusa': PAL_ENEMY_AEGIS_MEDUSA,
  'pal.enemy.siphon-leech': PAL_ENEMY_SIPHON_LEECH,
  'pal.enemy.gloomsquid': PAL_ENEMY_GLOOMSQUID,
  'pal.enemy.luremaw': PAL_ENEMY_LUREMAW,
  'pal.enemy.colonial-curtain': PAL_ENEMY_COLONIAL_CURTAIN,
  'pal.enemy.brood-bell': PAL_ENEMY_BROOD_BELL,

  'pal.boss.lantern-tyrant.primary': PAL_BOSS_LANTERN_TYRANT_PRIMARY,
  'pal.boss.lantern-tyrant.secondary': PAL_BOSS_LANTERN_TYRANT_SECONDARY,
  'pal.boss.abyssal-warden.primary': PAL_BOSS_ABYSSAL_WARDEN_PRIMARY,
  'pal.boss.abyssal-warden.secondary': PAL_BOSS_ABYSSAL_WARDEN_SECONDARY,
  'pal.boss.crown-medusa.primary': PAL_BOSS_CROWN_MEDUSA_PRIMARY,
  'pal.boss.crown-medusa.secondary': PAL_BOSS_CROWN_MEDUSA_SECONDARY,
  'pal.boss.hadal-sovereign.primary': PAL_BOSS_HADAL_SOVEREIGN_PRIMARY,
  'pal.boss.hadal-sovereign.secondary': PAL_BOSS_HADAL_SOVEREIGN_SECONDARY,

  'pal.status.burn': PAL_STATUS_BURN,
  'pal.status.poison': PAL_STATUS_POISON,
  'pal.status.shock': PAL_STATUS_SHOCK,
  'pal.status.slow': PAL_STATUS_SLOW,
  'pal.status.stun': PAL_STATUS_STUN,
  'pal.status.weaken': PAL_STATUS_WEAKEN,
  'pal.status.corrode': PAL_STATUS_CORRODE,
  'pal.status.bleed': PAL_STATUS_BLEED,

  'pal.ui.bg': PAL_UI_BG,
  'pal.ui.panel': PAL_UI_PANEL,
  'pal.ui.grid': PAL_UI_GRID,
  'pal.ui.text': PAL_UI_TEXT,
  'pal.ui.textdim': PAL_UI_TEXTDIM,
  'pal.ui.accent': PAL_UI_ACCENT,
  'pal.ui.decor': PAL_UI_DECOR,
  'pal.ui.hp': PAL_UI_HP,
  'pal.ui.xp': PAL_UI_XP,

  'pal.ui.gridmajor': PAL_UI_GRID_MAJOR,
  'pal.ui.core': PAL_UI_CORE,
  'pal.ui.outline': PAL_UI_OUTLINE,

  'pal.ui.range': PAL_UI_RANGE,

  'pal.skin.0': PAL_SKIN_0,
  'pal.skin.1': PAL_SKIN_1,
  'pal.skin.2': PAL_SKIN_2,
  'pal.skin.3': PAL_SKIN_3,
  'pal.skin.4': PAL_SKIN_4,
  'pal.skin.5': PAL_SKIN_5,
  'pal.skin.6': PAL_SKIN_6,
  'pal.skin.7': PAL_SKIN_7,
};

export type ColorblindPreset = 'off' | 'deuteranopia' | 'protanopia' | 'tritanopia';

/** §11.12 — accent remaps only. Every token absent here keeps its default hex. */
export const CB_REMAP: Readonly<Record<ColorblindPreset, Readonly<Record<string, string>>>> = {
  off: {},
  deuteranopia: {
    'pal.xp.mote': '#E8FF4D',
    'pal.ui.xp': '#E8FF4D',
  },
  protanopia: {
    'pal.xp.mote': '#E8FF4D',
    'pal.ui.xp': '#E8FF4D',
    'pal.health.pickup': '#FF9E5C',
    'pal.ui.hp': '#FF9E5C',
  },
  tritanopia: {
    // AMD-41: was #F2F9FF. §11.12 sent the tritanopia mote to neutral white, and the
    // Viénot projection puts neutral white ΔE 2.3 from `pal.player.core` — a tritanope
    // saw the XP motes and their own ship as the same colour. Saturating instead of
    // neutralising scores 17.1, which is 94% of the best any hex can do inside the
    // collapsed tritanope gamut, and it keeps the mote inside the spine §12 green-cyan
    // arc rather than spending the reserved-white code on a pickup.
    /*
     * Left at AMD-41's hex, and the reason is recorded because it was nearly changed.
     *
     * Simulating the shipped frame through Viénot shows a tritanope seeing blue enemies and
     * mint motes as one cyan, and the obvious response — darken the pickup so threats win on
     * brightness, the axis the deficiency leaves intact — looks right in a screenshot. It is
     * wrong. `tests/palette-separation.test.ts` measures the real thing, and a darker mote
     * (#12B39D) collapsed enemy×pickup separation to ΔE 5.3-13.5 against AMD-41's floor of
     * 14: moving the pickup DOWN in lightness walks it straight into the enemy cluster,
     * which sits low in the collapsed tritanope gamut. The hue is already at 94% of what the
     * gamut allows; the lightness is not free either.
     *
     * The separation this preset needs is achromatic and lives in the renderer instead — see
     * the full-strength enemy rim in `pushEnemy`, which no deficiency can take away.
     */
    'pal.xp.mote': '#2BFFE6',
    'pal.ui.xp': '#2BFFE6',
    'pal.hazard.fill': '#FF3D8A',
    'pal.ui.accent': '#FFE3A3',
  },
};

export const TOKENS: readonly string[] = Object.keys(PALETTE);
export const TOKEN_COUNT = TOKENS.length;

const TOKEN_INDEX = new Map<string, number>();
for (let i = 0; i < TOKENS.length; i++) TOKEN_INDEX.set(TOKENS[i], i);

/** Stable index for a token. Throws at boot on a typo rather than drawing black. */
export function tokenIndex(token: string): number {
  const i = TOKEN_INDEX.get(token);
  if (i === undefined) throw new Error(`palette: unknown token "${token}"`);
  return i;
}

/** Linear-space RGB triples, 3 floats per token, rebuilt when the preset changes. */
export const LINEAR = new Float32Array(TOKEN_COUNT * 3);

function srgbChannelToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Writes the linear RGB of a `#RRGGBB` string into `out` at `offset`. Boot-time only. */
export function hexToLinearRgb(hex: string, out: Float32Array, offset: number): void {
  const n = parseInt(hex.slice(1), 16);
  out[offset] = srgbChannelToLinear(((n >> 16) & 0xff) / 255);
  out[offset + 1] = srgbChannelToLinear(((n >> 8) & 0xff) / 255);
  out[offset + 2] = srgbChannelToLinear((n & 0xff) / 255);
}

let activePreset: ColorblindPreset = 'off';

/** Current hex for a token under the active preset. */
export function resolveToken(token: string): string {
  const remap = CB_REMAP[activePreset][token];
  return remap !== undefined ? remap : PALETTE[token];
}

function rebuildLinear(): void {
  for (let i = 0; i < TOKENS.length; i++) hexToLinearRgb(resolveToken(TOKENS[i]), LINEAR, i * 3);
}

export function setColorblindPreset(preset: ColorblindPreset): void {
  activePreset = preset;
  rebuildLinear();
}

export function getColorblindPreset(): ColorblindPreset {
  return activePreset;
}

rebuildLinear();

// --- colour analysis (validator + boot-time checks only; allocates, never called per frame) ---

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function hexToHsl(hex: string): Hsl {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 0xff) / 255;
  const g = ((n >> 8) & 0xff) / 255;
  const b = (n & 0xff) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;
  return { h, s, l };
}

export function hueDeg(hex: string): number {
  return hexToHsl(hex).h;
}

/** HSL lightness, the measure §11.15's "40% luminance" separation is stated against. */
export function lightness(hex: string): number {
  return hexToHsl(hex).l;
}

export function hueDelta(a: string, b: string): number {
  const d = Math.abs(hueDeg(a) - hueDeg(b));
  return Math.min(d, 360 - d);
}

/** Rec.709 relative luminance in linear space. Used by the photosafety validator. */
export function relativeLuminance(hex: string): number {
  const out = new Float32Array(3);
  hexToLinearRgb(hex, out, 0);
  return 0.2126 * out[0] + 0.7152 * out[1] + 0.0722 * out[2];
}

/**
 * OKLab coordinates. HSL hue is a poor separation metric on its own — it calls
 * #F2F9FF "hue 208°" when the colour is white — so the AMD-41 guard measures in a
 * perceptually uniform space and lets HSL hue stay the readable secondary rule.
 */
export function oklab(hex: string): [number, number, number] {
  const rgb = new Float32Array(3);
  hexToLinearRgb(hex, rgb, 0);
  const l = Math.cbrt(0.4122214708 * rgb[0] + 0.5363325363 * rgb[1] + 0.0514459929 * rgb[2]);
  const m = Math.cbrt(0.2119034982 * rgb[0] + 0.6806995451 * rgb[1] + 0.1073969566 * rgb[2]);
  const s = Math.cbrt(0.0883024619 * rgb[0] + 0.2817188376 * rgb[1] + 0.6299787005 * rgb[2]);
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ];
}

/** OKLab ΔE, scaled ×100 so the numbers read on the familiar ΔE scale. */
export function deltaE(a: string, b: string): number {
  const A = oklab(a);
  const B = oklab(b);
  return 100 * Math.hypot(A[0] - B[0], A[1] - B[1], A[2] - B[2]);
}

// --- §11.12 deficiency simulation (Viénot, Brettel & Mollon 1999) ---------------------

const RGB_TO_LMS = [
  0.31399022, 0.63951294, 0.04649755,
  0.15537241, 0.75789446, 0.08670142,
  0.01775239, 0.10944209, 0.87256922,
];
const LMS_TO_RGB = [
  5.47221206, -4.6419601, 0.16963708,
  -1.1252419, 2.29317094, -0.1678952,
  0.02980165, -0.19318073, 1.16364789,
];
/** Projection onto each dichromat's reduced colour plane. */
const DEFICIENCY_PLANE: Readonly<Record<string, readonly number[]>> = {
  protanopia: [0, 1.05118294, -0.05116099, 0, 1, 0, 0, 0, 1],
  deuteranopia: [1, 0, 0, 0.9513092, 0, 0.04264113, 0, 0, 1],
  tritanopia: [1, 0, 0, 0, 1, 0, -0.86744736, 1.86727089, 0],
};

function apply3(m: readonly number[], v: readonly number[], out: number[]): void {
  const a = v[0];
  const b = v[1];
  const c = v[2];
  out[0] = m[0] * a + m[1] * b + m[2] * c;
  out[1] = m[3] * a + m[4] * b + m[5] * c;
  out[2] = m[6] * a + m[7] * b + m[8] * c;
}

function linearToSrgbChannel(c: number): number {
  const v = c <= 0 ? 0 : c >= 1 ? 1 : c;
  return v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

/**
 * How `hex` appears to someone with `preset`'s deficiency. `'off'` returns it
 * unchanged, so the same call site covers the trichromat row of the matrix.
 */
export function simulateDeficiency(hex: string, preset: ColorblindPreset): string {
  const plane = DEFICIENCY_PLANE[preset];
  if (plane === undefined) return hex;
  const lin = new Float32Array(3);
  hexToLinearRgb(hex, lin, 0);
  const a: number[] = [0, 0, 0];
  const b: number[] = [0, 0, 0];
  apply3(RGB_TO_LMS, [lin[0], lin[1], lin[2]], a);
  apply3(plane, a, b);
  apply3(LMS_TO_RGB, b, a);
  let out = '#';
  for (let i = 0; i < 3; i++) {
    const n = Math.round(linearToSrgbChannel(a[i]) * 255);
    out += n.toString(16).padStart(2, '0').toUpperCase();
  }
  return out;
}

// --- content bindings ---

/** `enemies.json` id → palette token. §11.2.3 ratifies these one for one. */
export const ENEMY_TOKEN: Readonly<Record<string, string>> = {
  'enemy.driftfin': 'pal.enemy.driftfin',
  'enemy.glimmerfry': 'pal.enemy.glimmerfry',
  'enemy.dart-eel': 'pal.enemy.dart-eel',
  'enemy.ventspitter': 'pal.enemy.ventspitter',
  'enemy.bloom-jelly': 'pal.enemy.bloom-jelly',
  'enemy.basalt-crab': 'pal.enemy.basalt-crab',
  'enemy.spearnose': 'pal.enemy.spearnose',
  'enemy.nettle-anemone': 'pal.enemy.nettle-anemone',
  'enemy.aegis-medusa': 'pal.enemy.aegis-medusa',
  'enemy.siphon-leech': 'pal.enemy.siphon-leech',
  'enemy.gloomsquid': 'pal.enemy.gloomsquid',
  'enemy.luremaw': 'pal.enemy.luremaw',
  'enemy.colonial-curtain': 'pal.enemy.colonial-curtain',
  'enemy.brood-bell': 'pal.enemy.brood-bell',
};

/** `bosses.json` id → [primary, secondary] tokens. */
export const BOSS_TOKENS: Readonly<Record<string, readonly [string, string]>> = {
  'boss.lantern-tyrant': ['pal.boss.lantern-tyrant.primary', 'pal.boss.lantern-tyrant.secondary'],
  'boss.abyssal-warden': ['pal.boss.abyssal-warden.primary', 'pal.boss.abyssal-warden.secondary'],
  'boss.crown-medusa': ['pal.boss.crown-medusa.primary', 'pal.boss.crown-medusa.secondary'],
  'boss.hadal-sovereign': ['pal.boss.hadal-sovereign.primary', 'pal.boss.hadal-sovereign.secondary'],
};

/** `status-effects.json` id → palette token. */
export const STATUS_TOKEN: Readonly<Record<string, string>> = {
  'status.burn': 'pal.status.burn',
  'status.poison': 'pal.status.poison',
  'status.shock': 'pal.status.shock',
  'status.slow': 'pal.status.slow',
  'status.stun': 'pal.status.stun',
  'status.weaken': 'pal.status.weaken',
  'status.corrode': 'pal.status.corrode',
  'status.bleed': 'pal.status.bleed',
};

export interface SeparationFailure {
  enemy: string;
  boss: string;
  role: 'primary' | 'secondary';
  hueDelta: number;
  lightnessDelta: number;
}

/**
 * §11.15 validator: enemy hue vs boss primary/secondary, keep Δhue ≥ 30° or ΔL ≥ 40%.
 *
 * Returns the failing pairs rather than throwing. The ratified §11.2 table does not
 * satisfy this for every pair (the cool cluster deliberately reuses hues across the
 * enemy roster and the boss accents), so the test locks the known set instead of
 * asserting zero — a new hue that introduces a *new* collision still fails.
 */
export function bossEnemySeparationFailures(minHueDeg = 30, minLightness = 0.4): SeparationFailure[] {
  const out: SeparationFailure[] = [];
  for (const enemyId of Object.keys(ENEMY_TOKEN)) {
    const eHex = PALETTE[ENEMY_TOKEN[enemyId]];
    for (const bossId of Object.keys(BOSS_TOKENS)) {
      const pair = BOSS_TOKENS[bossId];
      for (let i = 0; i < 2; i++) {
        const bHex = PALETTE[pair[i]];
        const dh = hueDelta(eHex, bHex);
        const dl = Math.abs(lightness(eHex) - lightness(bHex));
        if (dh < minHueDeg && dl < minLightness) {
          out.push({
            enemy: enemyId,
            boss: bossId,
            role: i === 0 ? 'primary' : 'secondary',
            hueDelta: dh,
            lightnessDelta: dl,
          });
        }
      }
    }
  }
  return out;
}

/**
 * `pickups.json` category → the palette token the renderer draws it with.
 *
 * The XP row is the one that matters: `pal.xp.mote` is shared by all three
 * denominations, so a single hue carries the whole reward family.
 */
export const PICKUP_TOKEN: Readonly<Record<string, string>> = {
  xp: 'pal.xp.mote',
  health: 'pal.health.pickup',
  powerup: 'pal.ui.accent',
};

/**
 * AMD-41 separation floors — the check §11.14 and §11.15 both missed.
 *
 * Criterion 2 tested enemies against the *friendly* warm arc and §11.15 tested them
 * against bosses. Nothing ever tested an enemy against a pickup, which is why seven
 * archetypes shipped inside 45° of the XP mote.
 *
 * Two floors, because one metric cannot carry both jobs:
 *
 *  - `MIN_PICKUP_HUE_DEG = 55`. The readable rule, stated in the same HSL degrees
 *    §11.2 ratifies hues in. Every archetype a playtester called confusable measured
 *    ≤ 39.1°; the nearest one nobody flagged measured 50.6°, so the true boundary sits
 *    between them. 55° clears the known-good anchor with margin for the chroma that
 *    a 0.85-strength bloom washes out of a saturated emissive.
 *
 *  - `MIN_PICKUP_DELTA_E = 32`, XP family, trichromat. HSL hue alone is a liar near the
 *    achromatic axis: brood-bell sat 17.8° from the mote yet reads as a different
 *    colour because it is far darker, and white reports a hue at all. OKLab ΔE 32 is
 *    the same boundary expressed perceptually — the flagged pairs measured 12.0–30.1
 *    and the known-good dart-eel measured 34.7.
 *
 *  - `MIN_RESERVED_DELTA_E = 20` for the health and power-up families, trichromat. The
 *    strict floor is the XP floor because XP is the family that shares the enemies'
 *    half of the wheel, arrives four hundred at a time and blends additively. Health
 *    (red-pink) and power-ups (gold) already sit in reserved arcs a long way from cool,
 *    so hue does most of the work and the floor only has to be high enough to catch a
 *    real collision — which it is: the ratified roster failed it at 11.2.
 *
 *  - `MIN_PICKUP_DELTA_E_CB = 14` for the three §11.12 presets, each measured AFTER
 *    simulating its own deficiency. A dichromat projection collapses sRGB onto a
 *    plane, so distances shrink; over random pairs the 10th-percentile compression is
 *    0.45–0.59×, and 32 × 0.45 ≈ 14. Being an achievable floor rather than a
 *    comfortable one is the point: inside the tritanope gamut the best possible XP
 *    hex only reaches 18.1 against its nearest reserved neighbour.
 *
 * Enemies are checked against the PLAYER core on the same reserved floor. The near-white
 * end of the ratified roster measured ΔE 7.8 from `pal.player.core` under protanopia —
 * an enemy indistinguishable from the player's own body breaks AMD-7 R3 just as badly as
 * one indistinguishable from its loot.
 */
export const MIN_PICKUP_HUE_DEG = 55;
export const MIN_PICKUP_DELTA_E = 32;
export const MIN_RESERVED_DELTA_E = 20;
export const MIN_PICKUP_DELTA_E_CB = 14;

export interface PickupSeparationFailure {
  enemy: string;
  /** Pickup category from `PICKUP_TOKEN`, or `'player'` for the player-core check. */
  against: string;
  preset: ColorblindPreset;
  hueDelta: number;
  deltaE: number;
  floor: number;
}

/**
 * Every enemy × every pickup family × every §11.12 preset, simulated through that
 * preset's deficiency. Returns the failing pairs; the AMD-41 guard asserts it is empty.
 *
 * Allocates freely — a validator, never a frame.
 */
export function pickupSeparationFailures(
  minHueDeg = MIN_PICKUP_HUE_DEG,
  minDeltaE = MIN_PICKUP_DELTA_E,
  minDeltaECb = MIN_PICKUP_DELTA_E_CB,
  minReservedDeltaE = MIN_RESERVED_DELTA_E,
): PickupSeparationFailure[] {
  const out: PickupSeparationFailure[] = [];
  const presets: ColorblindPreset[] = ['off', 'deuteranopia', 'protanopia', 'tritanopia'];
  const targets = [...Object.keys(PICKUP_TOKEN).map((k) => [k, PICKUP_TOKEN[k]] as const), ['player', 'pal.player.core'] as const];
  for (const enemyId of Object.keys(ENEMY_TOKEN)) {
    const enemyHex = PALETTE[ENEMY_TOKEN[enemyId]];
    for (const preset of presets) {
      const remap = CB_REMAP[preset];
      const enemySeen = simulateDeficiency(enemyHex, preset);
      for (const [name, token] of targets) {
        const targetHex = remap[token] !== undefined ? remap[token] : PALETTE[token];
        const floor = preset !== 'off' ? minDeltaECb : name === 'xp' ? minDeltaE : minReservedDeltaE;
        const de = deltaE(enemySeen, simulateDeficiency(targetHex, preset));
        // The hue floor is a property of the ratified palette, so it is checked once,
        // on the trichromat row: a preset remap cannot rotate an enemy hue.
        const dh = hueDelta(enemyHex, targetHex);
        const hueFails = preset === 'off' && name === 'xp' && dh < minHueDeg;
        if (de < floor || hueFails) {
          out.push({ enemy: enemyId, against: name, preset, hueDelta: dh, deltaE: de, floor });
        }
      }
    }
  }
  return out;
}

/** Spine §12 family reservations, as hue arcs. Used by the §11.14 criterion-2 check. */
export const RESERVED_ARCS = {
  warm: { min: 20, max: 55 },
  xp: { min: 150, max: 175 },
  health: { min: 330, max: 360 },
  hazard: { min: 300, max: 320 },
} as const;
